import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_AIRCRAFT_DATA,
	URL_CMC_AIRCRAFT_HISTORY_DATA,
	URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
	URL_CMC_AIRFRAME_MRO_EVENT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productPageProps } from "../../common/types/product";

export default function AirframeMroProviderData({
	packageName,
	subscribeLink,
	testimonials,
}: productPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Explore 850+ MRO providers, customers they serve, where their facilities are located, and the type of airframe maintenance they provide (base, line + A checks, paint, etc.)."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"Access MRO provider profiles and their customer base",
							"Get insights into airframe maintenance locations and the type of services performed",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Airframe MRO Provider Profile",
						description:
							"Access Airframe MRO Provider profiles, including:",
						list: [
							"Status (active/inactive)",
							"Legal name",
							"Website",
							"Location",
							"Airframe MRO airports (airport name & IATA, maintenance type, and MRO subsidiary name) ",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-provider-data/mro-provider-profile-overview.jpg",
						},
					},
					{
						title: "Airframe MRO Events",
						description:
							"Access Airframe MRO Events within the Airframe MRO Provider profiles, including:",
						list: [
							"Date In",
							"Date Out",
							"Operator",
							"Airport",
							"Airframe MRO name",
							"Aircraft variant",
							"Aircraft registration",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-provider-data/mro-provider-profile-events.jpg",
						},
					},
					{
						title: "Airframe MRO Customers",
						description:
							"Get a comprehensive list of MRO customers within the Airframe MRO Provider profiles, including data on:",
						list: [
							"Airport (location where maintenance is performed)",
							"Operator (customer receiving maintenance services)",
							"Type of maintenance (base, line + A, etc.)",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-provider-data/mro-provider-profile-customers.jpg",
						},
					},
					{
						title: "Aircraft Variants",
						description:
							"Access the list of aircraft variants maintained by MRO provider.",
						visual: {
							url: "/static/img/product/commercial-airframe-mro-provider-data/mro-provider-profile-aircraft-variants.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for airframe MRO Providers by location, maintenance type, customer, aircraft type and more."
				slides={[
					{
						title: "Airframe MRO Locations",
						description: "Search for airframe MRO provider by:",
						list: [
							"Airport name ",
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-provider-data/airport-profile-airframe-mro-locations.jpg",
							title: "All MRO Providers located at Hartsfield-Jackson Atlanta International Airport",
						},
					},
					{
						title: "Airframe MRO",
						description: "Search for airframe MRO provider by:",
						list: [
							"Name or code of provider",
							"Maintenance type (base, paint, etc.)",
							"Status (active/inactive)",
							"Name or code of the operator customer",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-provider-data/mro-provider-search-by-name.jpg",
							title: "List of all Lufthansa Technik base maintenance customers",
						},
					},
					{
						title: "Aircraft Type",
						description: "Search for airframe MRO provider by:",
						list: [
							"Aircraft market group ",
							"Aircraft manufacturer",
							"Aircraft ",
							"Aircraft family",
							"Aircraft variant",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-provider-data/mro-provider-search-by-aircraft-type.jpg",
							title: "Complete list of MRO providers servicing widebody Boeing aircraft",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Airframe MRO Event Data",
						content:
							"Get insights into over 50,000 airframe maintenance events worldwide.",
						url: URL_CMC_AIRFRAME_MRO_EVENT_DATA,
					},
					{
						title: "Aircraft Data",
						content:
							"Get access to our extensive fleet database with over 82,000 aircraft currently being tracked .",
						url: URL_CMC_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft History Data",
						content:
							"Get access to full historical operator data down to an aircraft level.",
						url: URL_CMC_AIRCRAFT_HISTORY_DATA,
					},
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into aircraft ownership, management, valuation estimates, full lessor portfolios, and SPV-financed aircraft details.",
						url: URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Access MRO provider profiles and their customer base",
					"Get insights into airframe maintenance locations and the type of services performed",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Our Commercial Aviation Airframe MRO Provider Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"Commercial Aviation Airframe MRO Provider Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, Commercial Aviation Airframe MRO Provider
								Data can be purchased as a data feed for
								seamless integration into your own system.
								ch-aviation also provides full support for the
								integration process. To learn more about data
								feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "What is the source of the Airframe MRO Provider Data?",
						content:
							"The current data is based on research conducted over the past two years and includes a comprehensive view of the market.",
					},
					{
						title: "How frequently is the Airframe MRO Provider Data updated?",
						content: "The dataset is updated on a weekly basis.",
					},
				]}
			/>
		</>
	);
}
